import React, { useEffect, useMemo, useRef, useState } from 'react';
import { agentsLeftOutNote } from '../utils/chatEntries';
import { ResultView } from './workflowBuilderResultView';
import {
  NODE_TYPE_ICONS,
  NodeProgressCard,
} from './workflowBuilderProgressCard';


/**
 * Pure fingerprint of a run's node results: sorted `${id}:${status}`
 * pairs joined with commas.  Changes when a node appears or its status
 * transitions (e.g. running → completed under parallel execution), but
 * stays stable while only a node's progress array grows — so memos
 * keyed on it skip unrelated SSE ticks without missing status-only
 * transitions.
 *
 * @param {object|null|undefined} nodeResults - ``node_results_json``
 *   map of nodeId → result entry.
 * @returns {string} Stable fingerprint ('' when empty/absent).
 */
function nodeResultsFingerprint(nodeResults) {
  if (!nodeResults) return '';
  return Object.keys(nodeResults)
    .sort()
    .map((id) => `${id}:${nodeResults[id]?.status || ''}`)
    .join(',');
}


function RunStatusBadge({ runStatus }) {
  if (!runStatus) return null;
  const s = runStatus.status || 'pending';
  const icons = {
    pending: '⏳',
    running: '🔄',
    completed: '✅',
    failed: '❌',
  };
  return (
    <span className={`wf-run-status ${s}`}>
      {icons[s] || '⏳'} {s}
    </span>
  );
}


/* ─── Live run output: streaming progress + final result ─────── */

/**
 * Shows workflow run progress in real time.  During a run, completed
 * node cards stream in via SSE and the inferred active node is shown
 * with a spinner.  After the run, the "Result" tab shows the rendered
 * final output and "Raw" exposes the JSON.
 *
 * @param {object} runState  - Entry from ``activeRuns[workflowId]``.
 * @param {Array}  dagNodes  - ReactFlow nodes for the current workflow.
 * @param {Array}  dagEdges  - ReactFlow edges, used to infer active node.
 * @param {boolean} running  - True while status is pending/running.
 * @param {Function} onCancel  - Called when the Cancel button is clicked.
 * @param {Function} onDismiss - Called when the user dismisses output.
 */
function LiveRunOutput({ runState, dagNodes, dagEdges, running, onCancel, onDismiss }) {
  const [tab, setTab] = useState('progress');
  const prevRunning = useRef(running);

  /* Build id → {label, nodeType} map from the ReactFlow nodes.
     Must be declared before any early return (rules-of-hooks). */
  const nodeMeta = useMemo(() => {
    const map = {};
    (dagNodes || []).forEach((n) => {
      map[n.id] = {
        label: n.data?.label || n.id,
        nodeType: n.data?.config?.node_type || 'unknown',
      };
    });
    return map;
  }, [dagNodes]);

  /* Auto-advance to Result tab once the run completes with output. */
  const isTerminal = [
    'completed', 'failed', 'cancelled',
  ].includes(runState?.status);

  useEffect(() => {
    if (prevRunning.current && !running && isTerminal) {
      const hasOut =
        runState?.output_json &&
        Object.keys(runState.output_json).length > 0;
      if (hasOut) setTab('result');
    }
    prevRunning.current = running;
  }, [running, isTerminal, runState]);

  /* Stable string key: sorted `${id}:${status}` pairs from
     node_results_json.  Encoding the status means a status-only
     transition (running → completed with no new node key, common under
     parallel execution) still recomputes activeNodeIds, while
     progress-array growth alone produces the same string — so the
     traversal below is skipped on every unrelated SSE tick that merely
     creates a new runState object reference. */
  const nodeResultsKey = useMemo(
    () => nodeResultsFingerprint(runState?.node_results_json),
    [runState],
  );

  /* Infer which nodes are currently executing: a node is "active" when
     all its direct predecessors have status "completed" in the latest
     SSE snapshot but the node itself has no result yet.
     Must be declared before the early return (rules-of-hooks).
     Depends on nodeResultsKey (a stable primitive) rather than the
     full runState object so the O(nodes×edges) traversal is skipped
     when the result set hasn't changed. */
  const activeNodeIds = useMemo(() => {
    if (!running || !dagNodes || !dagEdges || !runState) return new Set();
    const nr = runState.node_results_json || {};
    const completedIds = new Set(
      Object.entries(nr)
        .filter(([, r]) => r?.status === 'completed')
        .map(([id]) => id),
    );
    const doneIds = new Set(Object.keys(nr));
    const active = new Set();
    for (const node of dagNodes) {
      if (doneIds.has(node.id)) continue;
      const predIds = dagEdges
        .filter((e) => e.target === node.id)
        .map((e) => e.source);
      const allPredsDone =
        predIds.length === 0 ||
        predIds.every((pid) => completedIds.has(pid));
      if (allPredsDone) active.add(node.id);
    }
    return active;
    // runState is read inside but nodeResultsKey gates when we rerun;
    // both update in the same render so the read is always fresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running, dagNodes, dagEdges, nodeResultsKey]);

  if (!runState) return null;

  const {
    node_results_json: nodeResults,
    output_json: output,
    error_message: errorMessage,
    runId,
  } = runState;

  const hasOutput = output && Object.keys(output).length > 0;
  const nodeResultEntries = Object.entries(nodeResults || {});
  const nodeErrors = nodeResultEntries
    .filter(([, r]) => r?.error)
    .map(([id, r]) => ({ id, error: r.error }));
  const nodeNotes = nodeResultEntries
    .map(([id, r]) => ({ id, note: agentsLeftOutNote(r?.agents_left_out) }))
    .filter(({ note }) => note);

  const tabBtn = (id, label) => (
    <button
      key={id}
      onClick={() => setTab(id)}
      style={{
        padding: '3px 10px',
        fontSize: '0.76rem',
        fontWeight: tab === id ? 700 : 400,
        border: 'none',
        borderBottom:
          tab === id
            ? '2px solid var(--accent-color, #3b82f6)'
            : '2px solid transparent',
        background: 'transparent',
        color:
          tab === id
            ? 'var(--text-color)'
            : 'var(--text-secondary)',
        cursor: 'pointer',
        borderRadius: 0,
      }}
    >
      {label}
    </button>
  );

  return (
    <div className="wf-run-output">
      {/* Header row */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 8,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span
            style={{
              fontWeight: 600,
              fontSize: '0.8rem',
              color: 'var(--text-secondary)',
            }}
          >
            Run Output
          </span>
          <RunStatusBadge runStatus={runState} />
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {running && onCancel && runId && (
            <button
              onClick={() => onCancel(runId)}
              style={{
                padding: '2px 8px',
                fontSize: '0.7rem',
                border: '1px solid var(--border-color)',
                borderRadius: 4,
                background: 'transparent',
                color: 'var(--error-text-color, #dc2626)',
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
          )}
          {isTerminal && onDismiss && (
            <button
              onClick={onDismiss}
              style={{
                padding: '2px 8px',
                fontSize: '0.7rem',
                border: '1px solid var(--border-color)',
                borderRadius: 4,
                background: 'transparent',
                color: 'var(--text-secondary)',
                cursor: 'pointer',
              }}
            >
              Dismiss
            </button>
          )}
        </div>
      </div>

      {/* Agents a node left out, whichever tab is open */}
      {nodeNotes.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          {nodeNotes.map(({ id, note }) => (
            <div
              key={id}
              style={{
                border: '1px solid var(--border-color)',
                borderRadius: 4,
                padding: '5px 8px',
                marginBottom: 4,
                fontSize: '0.74rem',
                color: 'var(--text-secondary)',
              }}
            >
              <span
                style={{
                  fontWeight: 700,
                  fontFamily: 'monospace',
                  display: 'block',
                }}
              >
                {id}
              </span>
              <span>{note}</span>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div
        style={{
          display: 'flex',
          borderBottom: '1px solid var(--border-color)',
          marginBottom: 12,
          gap: 0,
        }}
      >
        {tabBtn('progress', 'Progress')}
        {isTerminal && hasOutput && tabBtn('result', 'Result')}
        {isTerminal && hasOutput && tabBtn('raw', 'Raw JSON')}
      </div>

      {/* ── Progress tab ── */}
      {tab === 'progress' && (
        <div>
          {/* Node errors summary at top */}
          {nodeErrors.length > 0 && (
            <div style={{ marginBottom: 10 }}>
              {nodeErrors.map(({ id, error }) => (
                <div
                  key={id}
                  style={{
                    background: 'rgba(239,68,68,0.07)',
                    border: '1px solid rgba(239,68,68,0.22)',
                    borderRadius: 4,
                    padding: '5px 8px',
                    marginBottom: 4,
                    fontSize: '0.74rem',
                  }}
                >
                  <span
                    style={{
                      fontWeight: 700,
                      fontFamily: 'monospace',
                      color: 'var(--error-text-color)',
                      display: 'block',
                    }}
                  >
                    {id}
                  </span>
                  <span style={{ color: 'var(--error-text-color)' }}>
                    {error}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Top-level run error (no node-level errors) */}
          {errorMessage && nodeErrors.length === 0 && (
            <div
              style={{
                color: 'var(--error-text-color)',
                fontSize: '0.8rem',
                marginBottom: 8,
              }}
            >
              {errorMessage}
            </div>
          )}

          {/* Completed node cards */}
          {nodeResultEntries.map(([nodeId, result]) => {
            const meta = nodeMeta[nodeId] || {
              label: nodeId,
              nodeType: 'unknown',
            };
            return (
              <NodeProgressCard
                key={nodeId}
                label={meta.label}
                nodeType={meta.nodeType}
                result={result}
                defaultExpanded={false}
              />
            );
          })}

          {/* Active node cards — named stages currently executing */}
          {running && activeNodeIds.size > 0 && (
            Array.from(activeNodeIds).map((nodeId) => {
              const meta = nodeMeta[nodeId] || {
                label: nodeId,
                nodeType: 'unknown',
              };
              return (
                <div
                  key={nodeId}
                  style={{
                    borderLeft: '3px solid var(--accent-color, #3b82f6)',
                    paddingLeft: 10,
                    marginBottom: 10,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    <span
                      style={{
                        display: 'inline-block',
                        width: 7,
                        height: 7,
                        borderRadius: '50%',
                        background: 'var(--accent-color, #3b82f6)',
                        flexShrink: 0,
                        animation: 'wf-pulse 1.4s ease-in-out infinite',
                      }}
                    />
                    <span
                      style={{
                        fontSize: '0.78rem',
                        fontWeight: 600,
                        color: 'var(--text-color)',
                      }}
                    >
                      {meta.label}
                    </span>
                    <span
                      style={{
                        fontSize: '0.63rem',
                        color: 'var(--text-secondary)',
                      }}
                    >
                      {NODE_TYPE_ICONS[meta.nodeType] || '◻'} {meta.nodeType}
                    </span>
                  </div>
                  <div
                    style={{
                      fontSize: '0.72rem',
                      color: 'var(--text-secondary)',
                      paddingLeft: 14,
                      marginTop: 2,
                    }}
                  >
                    Running…
                  </div>
                </div>
              );
            })
          )}

          {/* Fallback spinner when active node cannot be determined yet */}
          {running && activeNodeIds.size === 0 && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 0 4px',
                fontSize: '0.78rem',
                color: 'var(--text-secondary)',
              }}
            >
              <span
                style={{
                  display: 'inline-block',
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: 'var(--accent-color, #3b82f6)',
                  animation: 'wf-pulse 1.4s ease-in-out infinite',
                }}
              />
              Starting…
            </div>
          )}

          {!running && nodeResultEntries.length === 0 && (
            <p
              style={{
                fontSize: '0.8rem',
                color: 'var(--text-secondary)',
              }}
            >
              No node results yet.
            </p>
          )}
        </div>
      )}

      {/* ── Result tab ── */}
      {tab === 'result' && isTerminal && hasOutput && (
        <ResultView output={output} />
      )}

      {/* ── Raw JSON tab ── */}
      {tab === 'raw' && isTerminal && hasOutput && (
        <pre
          style={{
            margin: 0,
            fontSize: '0.72rem',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {JSON.stringify(output, null, 2)}
        </pre>
      )}
    </div>
  );
}


/* ─── Run input dialog ──────────────────────────────────────── */

function RunInputDialog({ inputNodes, onConfirm, onCancel }) {
  const [values, setValues] = useState(() => {
    const init = {};
    inputNodes.forEach((n) => {
      const varName = n.data?.config?.variable_name || '';
      init[varName] = n.data?.config?.default_value || '';
    });
    return init;
  });

  const set = (key) => (val) =>
    setValues((prev) => ({ ...prev, [key]: val }));

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.4)',
        zIndex: 2000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        style={{
          background: 'var(--modal-background)',
          border: '1px solid var(--border-color)',
          borderRadius: 10,
          padding: 24,
          minWidth: 340,
          maxWidth: 500,
          boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
        }}
      >
        <h3
          style={{
            margin: '0 0 16px',
            fontSize: '1rem',
            color: 'var(--text-color)',
          }}
        >
          Provide Input Values
        </h3>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
            maxHeight: 320,
            overflowY: 'auto',
          }}
        >
          {inputNodes.map((n) => {
            const varName = n.data?.config?.variable_name || '';
            const desc = n.data?.config?.description || '';
            return (
              <div key={n.id} className="wf-field">
                <label className="wf-label">
                  {varName}
                  {desc && (
                    <span
                      style={{
                        fontWeight: 400,
                        color: 'var(--text-secondary)',
                      }}
                    >
                      {' '}
                      — {desc}
                    </span>
                  )}
                </label>
                <textarea
                  className="wf-textarea"
                  rows={3}
                  value={values[varName] || ''}
                  onChange={(e) => set(varName)(e.target.value)}
                  placeholder={
                    n.data?.config?.default_value || 'Enter value…'
                  }
                />
              </div>
            );
          })}
        </div>
        <div
          style={{
            display: 'flex',
            gap: 10,
            justifyContent: 'flex-end',
            marginTop: 16,
          }}
        >
          <button className="wf-btn" onClick={onCancel}>
            Cancel
          </button>
          <button
            className="wf-btn wf-btn-run"
            onClick={() => onConfirm(values)}
          >
            ▶ Run
          </button>
        </div>
      </div>
    </div>
  );
}


export {
  nodeResultsFingerprint,
  RunStatusBadge,
  LiveRunOutput,
  RunInputDialog,
};
